import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";
import classnames from "classnames";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import useShopeeWorkspace from "./useShopeeWorkspace";
import ShopeeMappingPanel from "./ShopeeMappingPanel";
import ShopeeInventoryPanel from "./ShopeeInventoryPanel";
import ShopeePricingPanel from "./ShopeePricingPanel";
import ShopeeShippingPanel from "./ShopeeShippingPanel";
import ShopeeConnectionsPanel from "./ShopeeConnectionsPanel";
import ShopeeIssueCenter from "./ShopeeIssueCenter";
import {
  AutomationPanel,
  CatalogPanel,
  FinancePanel,
  LogsPanel,
  OrdersPanel,
  OverviewPanel,
  ReturnsPanel,
} from "./ShopeePanels";
import "./ShopeeOverview.scss";

const tabGroups = [
  {
    titleId: "shopee.workspace.group.overview",
    items: [
      {
        key: "overview",
        labelId: "shopee.workspace.tab.overview",
        icon: "simple-icon-pie-chart",
      },
    ],
  },
  {
    titleId: "shopee.workspace.group.catalog",
    items: [
      {
        key: "orders",
        labelId: "shopee.workspace.tab.orders",
        icon: "simple-icon-basket",
      },
      {
        key: "products",
        labelId: "shopee.workspace.tab.products",
        icon: "simple-icon-layers",
      },
      {
        key: "mappings",
        labelId: "shopee.workspace.tab.mappings",
        icon: "simple-icon-link",
      },
      {
        key: "inventory",
        labelId: "shopee.workspace.tab.inventory",
        icon: "simple-icon-social-dropbox",
      },
      {
        key: "pricing",
        labelId: "shopee.workspace.tab.pricing",
        icon: "simple-icon-tag",
      },
    ],
  },
  {
    titleId: "shopee.workspace.group.logistics",
    items: [
      {
        key: "shipping",
        labelId: "shopee.workspace.tab.shipping",
        icon: "simple-icon-plane",
      },
      {
        key: "returns",
        labelId: "shopee.workspace.tab.returns",
        icon: "simple-icon-action-undo",
      },
      {
        key: "reconciliation",
        labelId: "shopee.workspace.tab.reconciliation",
        icon: "simple-icon-calculator",
      },
    ],
  },
  {
    titleId: "shopee.workspace.group.system",
    items: [
      {
        key: "issues",
        labelId: "shopee.workspace.tab.issues",
        icon: "simple-icon-exclamation",
      },
      {
        key: "connections",
        labelId: "shopee.workspace.tab.connections",
        icon: "simple-icon-share",
      },
      {
        key: "automation",
        labelId: "shopee.workspace.tab.automation",
        icon: "simple-icon-refresh",
      },
      {
        key: "logs",
        labelId: "shopee.workspace.tab.logs",
        icon: "simple-icon-list",
      },
    ],
  },
];

const tabCapability = {
  orders: "shopee_order",
  products: "shopee_catalog",
  mappings: "shopee_catalog",
  inventory: "shopee_inventory",
  pricing: "shopee_catalog",
  shipping: "shopee_fulfillment",
  returns: "shopee_return",
  reconciliation: "shopee_finance",
  issues: "shopee_monitor",
  connections: "shopee_shop",
  automation: "shopee_shop",
  logs: "shopee_monitor",
};

const oauthErrorMessageIds = {
  missing_params: "shopee.oauth.error.missing-params",
  invalid_state: "shopee.oauth.error.invalid-state",
  not_configured: "shopee.oauth.error.not-configured",
  shop_already_connected: "shopee.oauth.error.shop-already-connected",
  callback_failed: "shopee.oauth.error.callback-failed",
  upgrade_required: "shopee.oauth.error.upgrade-required",
};

const Panel = ({ tab }) => {
  if (tab === "overview") return <OverviewPanel />;
  if (tab === "orders") return <OrdersPanel />;
  if (tab === "products") return <CatalogPanel />;
  if (tab === "mappings") return <ShopeeMappingPanel />;
  if (tab === "inventory") return <ShopeeInventoryPanel />;
  if (tab === "pricing") return <ShopeePricingPanel />;
  if (tab === "shipping") return <ShopeeShippingPanel />;
  if (tab === "returns") return <ReturnsPanel />;
  if (tab === "reconciliation") return <FinancePanel />;
  if (tab === "issues") return <ShopeeIssueCenter />;
  if (tab === "connections") return <ShopeeConnectionsPanel />;
  if (tab === "automation") return <AutomationPanel />;
  return <LogsPanel />;
};

export default injectIntl(function ShopeeWorkspace({ intl }) {
  const workspace = useShopeeWorkspace(intl);
  const [oauthResult, setOauthResult] = useState(null);
  const canManageShops =
    workspace.capabilities.shopee_shop &&
    workspace.capabilities.shopee_shop.write;
  const activeCapability =
    workspace.capabilities[tabCapability[workspace.activeTab]];
  const isConnected = workspace.shops && workspace.shops.length > 0;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const success = url.searchParams.get("shopee_success");
    const errorCode = url.searchParams.get("shopee_error");
    if (success === "1") {
      setOauthResult({
        color: "success",
        messageId: "shopee.oauth.success",
      });
    } else if (errorCode) {
      setOauthResult({
        color: "danger",
        messageId:
          oauthErrorMessageIds[errorCode] || "shopee.oauth.error.generic",
      });
    }
    if (success !== null || errorCode !== null) {
      url.searchParams.delete("shopee_success");
      url.searchParams.delete("shopee_error");
      window.history.replaceState(
        window.history.state,
        document.title,
        `${url.pathname}${url.search}${url.hash}`,
      );
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const mainEl = document.querySelector("main");
      if (mainEl) mainEl.scrollTop = 0;
    }
  }, [workspace.activeTab]);

  if (workspace.loadingShops && !workspace.error) {
    return (
      <ShopeeWorkspaceContext.Provider value={workspace}>
        <div className="shopee-workspace">
          <Card>
            <CardBody className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status" />
              <div>
                <IntlMessages id="shopee.workspace.loading" />
              </div>
            </CardBody>
          </Card>
        </div>
      </ShopeeWorkspaceContext.Provider>
    );
  }

  if (!isConnected) {
    return (
      <ShopeeWorkspaceContext.Provider value={workspace}>
        <div className="shopee-workspace">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div>
              <h1 className="mb-1">
                <IntlMessages id="shopee.workspace.title" />
              </h1>
              <p className="text-muted mb-0">
                <IntlMessages id="shopee.workspace.subtitle" />
              </p>
            </div>
            {canManageShops ? (
              <Button
                color="primary"
                onClick={workspace.connectShop}
                disabled={workspace.connecting}
              >
                <IntlMessages id="shopee.connect.btn" />
              </Button>
            ) : null}
          </div>

          {oauthResult ? (
            <Alert
              color={oauthResult.color}
              toggle={() => setOauthResult(null)}
            >
              <IntlMessages id={oauthResult.messageId} />
            </Alert>
          ) : null}
          {workspace.error || workspace.connectError ? (
            <Alert color="danger">
              {workspace.error || workspace.connectError}
            </Alert>
          ) : null}
          {workspace.notice ? (
            <Alert color="info" toggle={workspace.clearNotice}>
              {workspace.notice}
            </Alert>
          ) : null}

          <Row className="justify-content-center my-4">
            <Col xs="12" md="10" lg="8">
              <Card className="text-center shadow-sm">
                <CardBody className="p-5">
                  <div className="mb-3 text-primary">
                    <i className="simple-icon-link display-3" />
                  </div>
                  <h3 className="mb-3 font-weight-bold">
                    <IntlMessages id="shopee.workspace.connect-required.title" />
                  </h3>
                  <p
                    className="text-muted mb-4"
                    style={{ fontSize: "1.05rem" }}
                  >
                    <IntlMessages id="shopee.workspace.connect-required.desc" />
                  </p>

                  <div className="row text-left mb-4 justify-content-center">
                    <div className="col-sm-10 col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="simple-icon-check text-success mr-2 font-weight-bold" />
                        <span>
                          <IntlMessages id="shopee.workspace.connect-required.feature.orders" />
                        </span>
                      </div>
                    </div>
                    <div className="col-sm-10 col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="simple-icon-check text-success mr-2 font-weight-bold" />
                        <span>
                          <IntlMessages id="shopee.workspace.connect-required.feature.mapping" />
                        </span>
                      </div>
                    </div>
                    <div className="col-sm-10 col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="simple-icon-check text-success mr-2 font-weight-bold" />
                        <span>
                          <IntlMessages id="shopee.workspace.connect-required.feature.shipping" />
                        </span>
                      </div>
                    </div>
                    <div className="col-sm-10 col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="simple-icon-check text-success mr-2 font-weight-bold" />
                        <span>
                          <IntlMessages id="shopee.workspace.connect-required.feature.finance" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    color="primary"
                    size="lg"
                    className="px-4 py-2 font-weight-bold"
                    onClick={workspace.connectShop}
                    disabled={workspace.connecting}
                  >
                    {workspace.connecting ? (
                      <IntlMessages id="shopee.workspace.connect-required.connecting" />
                    ) : (
                      <IntlMessages id="shopee.workspace.connect-required.cta" />
                    )}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </ShopeeWorkspaceContext.Provider>
    );
  }

  return (
    <ShopeeWorkspaceContext.Provider value={workspace}>
      <div className="shopee-workspace">
        {oauthResult ? (
          <Alert color={oauthResult.color} toggle={() => setOauthResult(null)}>
            <IntlMessages id={oauthResult.messageId} />
          </Alert>
        ) : null}
        {workspace.error || workspace.connectError ? (
          <Alert color="danger">
            {workspace.error || workspace.connectError}
          </Alert>
        ) : null}
        {workspace.notice ? (
          <Alert color="info" toggle={workspace.clearNotice}>
            {workspace.notice}
          </Alert>
        ) : null}
        {activeCapability && activeCapability.provider === "not_granted" ? (
          <Alert color="warning">
            <IntlMessages id="shopee.workspace.scope-not-granted" />{" "}
            {(activeCapability.required_scopes || []).join(", ")}
          </Alert>
        ) : null}

        <Row>
          <Col xs="12" md="3" lg="2" className="mb-3 mb-md-0 pr-md-2">
            <Card
              className="shopee-menu-card border-0 shadow-sm overflow-hidden"
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
              }}
            >
              {/* Header: Shop Selection */}
              <div
                className="p-2 border-bottom"
                style={{ borderColor: "#f1f5f9", background: "#f2f2f2" }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                  <div
                    className="d-flex align-items-center text-muted font-weight-bold"
                    style={{
                      fontSize: "9.5px",
                      letterSpacing: "0.6px",
                      color: "#94a3b8",
                      textTransform: "uppercase",
                    }}
                  >
                    <i
                      className="simple-icon-bag mr-1 text-primary"
                      style={{ fontSize: "11px" }}
                    />
                    <span>
                      <IntlMessages id="shopee.workspace.sidebar.shops-header" />
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    {canManageShops ? (
                      <Button
                        color="link"
                        className="p-0 text-muted mr-2"
                        title={intl.formatMessage({
                          id: "shopee.workspace.sidebar.add-shop-title",
                        })}
                        onClick={workspace.connectShop}
                        disabled={workspace.connecting}
                        style={{ fontSize: "12px", textDecoration: "none" }}
                      >
                        <i className="simple-icon-plus" />
                      </Button>
                    ) : null}
                    <Button
                      color="link"
                      className="p-0 text-muted"
                      title={intl.formatMessage({
                        id: "shopee.workspace.sidebar.refresh-title",
                      })}
                      onClick={workspace.load}
                      disabled={workspace.loading}
                      style={{ fontSize: "12px", textDecoration: "none" }}
                    >
                      <i
                        className={classnames("simple-icon-refresh", {
                          "fa-spin": workspace.loading,
                        })}
                      />
                    </Button>
                  </div>
                </div>
                <Input
                  type="select"
                  bsSize="sm"
                  value={workspace.selectedShopId}
                  onChange={(event) =>
                    workspace.setSelectedShopId(event.target.value)
                  }
                  className="font-weight-medium"
                  style={{
                    fontSize: "11.5px",
                    height: "32px",
                    padding: "0 8px",
                    lineHeight: "30px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    cursor: "pointer",
                  }}
                  aria-label="Shopee shop"
                >
                  {!workspace.shops.length ? (
                    <option value="">
                      {intl.formatMessage({ id: "shopee.no-shop" })}
                    </option>
                  ) : (
                    <option value="ALL">
                      {intl.formatMessage(
                        { id: "shopee.workspace.sidebar.all-shops" },
                        { count: workspace.shops.length }
                      )}
                    </option>
                  )}
                  {workspace.shops.map((shop) => (
                    <option key={shop.shop_id} value={shop.shop_id}>
                      {shop.shop_name ||
                        intl.formatMessage(
                          { id: "shopee.workspace.sidebar.shop-fallback" },
                          { shopId: shop.shop_id }
                        )}{" "}
                      ({shop.shop_region || "VN"})
                    </option>
                  ))}
                </Input>
              </div>

              {/* Body Navigation */}
              <CardBody className="p-2">
                {tabGroups.map((group, gIdx) => (
                  <div
                    key={group.titleId}
                    className={classnames({ "mt-3": gIdx > 0 })}
                  >
                    <div
                      className="px-2 mb-2 font-weight-bold text-muted d-flex align-items-center"
                      style={{
                        fontSize: "9.5px",
                        letterSpacing: "0.6px",
                        color: "#94a3b8",
                        textTransform: "uppercase",
                      }}
                    >
                      <span>
                        <IntlMessages id={group.titleId} />
                      </span>
                    </div>
                    <Nav vertical className="flex-column">
                      {group.items.map((item) => {
                        const isActive = workspace.activeTab === item.key;
                        return (
                          <NavItem key={item.key} className="mb-1">
                            <NavLink
                              href="#"
                              className={classnames(
                                "shopee-sidebar-nav-link d-flex align-items-center rounded",
                                {
                                  active: isActive,
                                },
                              )}
                              onClick={(event) => {
                                event.preventDefault();
                                workspace.setActiveTab(item.key);
                                workspace.setSelection([]);
                                if (typeof window !== "undefined") {
                                  window.scrollTo({
                                    top: 0,
                                    left: 0,
                                    behavior: "smooth",
                                  });
                                  if (document.documentElement)
                                    document.documentElement.scrollTop = 0;
                                  if (document.body)
                                    document.body.scrollTop = 0;
                                  const mainEl = document.querySelector("main");
                                  if (mainEl) mainEl.scrollTop = 0;
                                }
                              }}
                            >
                              <i
                                className={classnames(
                                  item.icon,
                                  "mr-2 shopee-sidebar-nav-icon",
                                  {
                                    "text-danger":
                                      !isActive && item.key === "issues",
                                  },
                                )}
                              />
                              <span className="flex-grow-1 text-truncate">
                                <IntlMessages id={item.labelId} />
                              </span>
                              <i
                                className={classnames(
                                  "simple-icon-arrow-right ml-1 shopee-sidebar-nav-arrow",
                                  {
                                    "is-visible": isActive,
                                  },
                                )}
                              />
                            </NavLink>
                          </NavItem>
                        );
                      })}
                    </Nav>
                  </div>
                ))}
              </CardBody>

              {/* Status Footer */}
              <div
                className="p-2 border-top"
                style={{ borderColor: "#f1f5f9", background: "#f8fafc" }}
              >
                <div
                  className="d-flex align-items-center justify-content-between p-2 rounded"
                  style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}
                >
                  <div className="d-flex align-items-center">
                    <span
                      className="mr-2"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: isConnected ? "#10b981" : "#f59e0b",
                        display: "inline-block",
                        boxShadow: isConnected
                          ? "0 0 6px rgba(16, 185, 129, 0.5)"
                          : "none",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#334155",
                      }}
                    >
                      {isConnected ? (
                        <IntlMessages
                          id="shopee.workspace.sidebar.connected-count"
                          values={{ count: workspace.shops.length }}
                        />
                      ) : (
                        <IntlMessages id="shopee.workspace.sidebar.not-connected" />
                      )}
                    </span>
                  </div>
                  <Badge
                    color={isConnected ? "outline-success" : "outline-warning"}
                    pill
                    style={{ fontSize: "9px", padding: "2px 6px" }}
                  >
                    {isConnected ? "ONLINE" : "OFFLINE"}
                  </Badge>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs="12" md="9" lg="10" className="pl-md-2">
            <div className="position-relative" aria-busy={workspace.loading}>
              <Panel
                key={`${workspace.activeTab}:${workspace.selectedShopId}`}
                tab={workspace.activeTab}
              />
              {workspace.loading ? (
                <div
                  className="position-absolute"
                  style={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 20,
                    cursor: "progress",
                    background: "rgba(255, 255, 255, 0.45)",
                    backdropFilter: "blur(1px)",
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="position-absolute d-flex align-items-center rounded shadow-sm px-2 py-1 text-muted small"
                    style={{
                      top: 8,
                      right: 8,
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <span
                      className="spinner-border spinner-border-sm mr-2 text-primary"
                      aria-hidden="true"
                    />
                    <IntlMessages id="shopee.workspace.loading" />
                  </div>
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      </div>
    </ShopeeWorkspaceContext.Provider>
  );
});
